import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { config } from '../../config';
import { prepareHeaders } from './utils';

interface VinResponse {
    vin: string;
    brand: string;
}

export interface DeviceSharingAccessRules {
    driverstate_rules: string[];
    assetstate_rules: string[];
}

export interface DeviceSharing {
    id: string;
    association_id: string;
    shared_association_id: string;
    asset_id: string;
    device_id: string;
    shared_asset_id: string;
    shared_account_id: string;
    shared_account_name?: string | null;
    sharing_started_at?: string | null;
    shared_access_rules?: DeviceSharingAccessRules;
}

// biome-ignore lint/suspicious/noExplicitAny: suppress out of convenience during migration to biome
const isVinResponse = (foo: any): foo is VinResponse => typeof foo.vin === 'string' && typeof foo.brand === 'string';

const isDeviceSharing = (value: unknown): value is DeviceSharing => {
    if (!value || typeof value !== 'object') {
        return false;
    }

    const sharing = value as Partial<DeviceSharing>;

    return (
        typeof sharing.id === 'string' &&
        typeof sharing.association_id === 'string' &&
        typeof sharing.shared_association_id === 'string' &&
        typeof sharing.asset_id === 'string' &&
        typeof sharing.device_id === 'string' &&
        typeof sharing.shared_asset_id === 'string' &&
        typeof sharing.shared_account_id === 'string' &&
        (sharing.shared_account_name === undefined || sharing.shared_account_name === null || typeof sharing.shared_account_name === 'string') &&
        (sharing.sharing_started_at === undefined || sharing.sharing_started_at === null || typeof sharing.sharing_started_at === 'string') &&
        (sharing.shared_access_rules === undefined || (typeof sharing.shared_access_rules === 'object' && sharing.shared_access_rules !== null))
    );
};

const getDeviceSharingsFromResponse = (rawResult: unknown): DeviceSharing[] => {
    if (Array.isArray(rawResult)) {
        return rawResult.filter(isDeviceSharing);
    }

    if (isDeviceSharing(rawResult)) {
        return [rawResult];
    }

    if (rawResult && typeof rawResult === 'object' && 'items' in rawResult) {
        const items = (rawResult as { items?: unknown }).items;

        return Array.isArray(items) ? items.filter(isDeviceSharing) : [];
    }

    return [];
};

export const assetsApi = createApi({
    reducerPath: 'assetsApi',
    baseQuery: fetchBaseQuery({ baseUrl: config.backend.assetAdmin, prepareHeaders }),
    endpoints: build => ({
        getBrand: build.query<string | undefined, { vin: string }>({
            query: ({ vin }) => ({
                url: `/vins/${vin}`,
            }),
            transformResponse: rawResult => {
                if (isVinResponse(rawResult)) {
                    return rawResult.brand;
                } else {
                    return undefined;
                }
            },
        }),
        getDeviceSharings: build.query<DeviceSharing[], void>({
            query: () => ({
                url: '/device-sharings',
                params: {
                    limit: 100,
                },
            }),
            transformResponse: getDeviceSharingsFromResponse,
        }),
    }),
});

export const { useGetDeviceSharingsQuery, useLazyGetBrandQuery } = assetsApi;
